<!--
  FieldGrid — declarative layout for detail-view form fields.

  Turns a nested array of field names into CSS-grid rows (.field / .field-pair /
  .field-triple), with built-in default rendering (ItemDetailField + FieldComponent).

  CELL TYPES
    'name'                    → field            — default ItemDetailField + FieldComponent
    'slot:fc:is_template'     → fcSlot           — ItemDetailField wrapper, slot replaces FieldComponent
    'slot:screenFields'       → rowSlot          — raw slot content, no wrapper at all
    'widget:instrumentData'   → compositeWidget  — composite widget, receives dv directly
    null / undefined           → placeholder      — empty div for grid alignment

  COMPOSITE WIDGETS
    Some fields manage multiple record fields (cross-field editing).
    These receive `dv` directly instead of going through FieldComponent.
    Register them in COMPOSITE_WIDGETS below. They render without an
    ItemDetailField wrapper (they provide their own labels internally).

    Use 'widget:name' in the :fields array to render a composite widget.

  ROW SHAPES (each entry in :fields is a row)
    ['name']                          → <div class="field">
    ['state', 'slot:fc:is_template']  → <div class="field-pair">
    ['a', 'b', 'c']                   → <div class="field-triple">
    'slot:screenFields'               → standalone rowSlot, no .field wrapper

  SLOT RESOLUTION (per cell, in priority order)
    Slots come from two sources, merged internally (direct $slots take priority):
      - Direct named slots on <FieldGrid> (e.g. #instrument from ImagingDescription)
      - Forwarded slots via :cell-slots prop (e.g. $slots from a grandparent)

    rowSlot:
      Renders raw slot content. No ItemDetailField, no .field div.
      Used for section-level insertions (e.g. a nested FieldGrid).
      If no slot provided → nothing rendered.

    fcSlot:
      ItemDetailField wrapper provided by GridCell. Slot replaces the inner
      FieldComponent. Used for fields whose visibility is controlled by the parent
      (e.g. v-if="isSuperUser" on the slot template).
      If no slot provided → nothing rendered.

    field (regular):
      ItemDetailField wrapper always provided.
        1. slots[fieldName]        — replaces inner FieldComponent
        2. slots.field({ name })   — scoped slot for bulk custom rendering
        3. Default: FieldComponent wired to dv

  PROPS
    :fields             Array of rows (see ROW SHAPES / CELL TYPES above)
    :dv                 Detail-view object (modelAttributes, editedRecord, isEditing, edit)
    :cell-slots         Object of slot functions forwarded from an ancestor (typically $slots)
    :validation-errors  { fieldName: 'message' } → forwarded as :error-message on default FieldComponent
    :disabled-fields    { fieldName: true/false } → forwarded as :disabled on default FieldComponent

  EXAMPLE
    <FieldGrid
      :fields="[
        ['name'],
        ['state', 'slot:fc:is_template'],
        'slot:screenFields',
        ['widget:instrumentData'],
        ['description'],
      ]"
      :dv="dv"
      :cell-slots="$slots"
    />
-->
<template>
  <div class="form-fields edge-to-edge">
    <template v-for="(row, i) in normalizedFields" :key="i">
      <!--
        Standalone rowSlot (e.g. 'slot:screenFields'):
        No .field wrapper, no ItemDetailField — the slot content provides its own structure.
      -->
      <component
        :is="() => mergedSlots[row.cells[0].name]?.()"
        v-if="row.cells.length === 1 && row.cells[0].type === 'rowSlot'"
      />
      <!-- Composite widget: no .field wrapper — they provide their own layout -->
      <GridCell
        v-else-if="
          row.cells.length === 1 && row.cells[0].type === 'compositeWidget'
        "
        :cell="row.cells[0]"
        :dv="dv"
        :slots="mergedSlots"
        :validation-errors="validationErrors"
        :disabled-fields="disabledFields"
      />
      <!-- Single-field row: .field wrapper -->
      <div
        v-else-if="row.cells.length === 1"
        class="field"
        :class="{ editing: dv.isEditing }"
      >
        <GridCell
          :key="getCellKey(row.cells[0], i)"
          :cell="row.cells[0]"
          :dv="dv"
          :slots="mergedSlots"
          :validation-errors="validationErrors"
          :disabled-fields="disabledFields"
        />
      </div>
      <!-- Spanning row: single field stretched across the multi-col wrapper -->
      <div v-else-if="row.isSpan" :class="ROW_CLASS[row.rawLength]">
        <div class="field field-span" :class="{ editing: dv.isEditing }">
          <GridCell
            :cell="row.cells.find((c) => c.type !== 'placeholder')"
            :dv="dv"
            :slots="mergedSlots"
            :validation-errors="validationErrors"
            :disabled-fields="disabledFields"
          />
        </div>
      </div>
      <!-- Multi-field row: .field-pair or .field-triple wrapper -->
      <div v-else :class="ROW_CLASS[row.rawLength]">
        <div
          v-for="(cell, cellIdx) in row.cells"
          :key="cell.name ?? `placeholder-${cellIdx}`"
          class="field"
          :class="{
            editing: dv.isEditing,
            placeholder: cell.type === 'placeholder',
          }"
        >
          <GridCell
            :cell="cell"
            :dv="dv"
            :slots="mergedSlots"
            :validation-errors="validationErrors"
            :disabled-fields="disabledFields"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  computed,
  defineAsyncComponent,
  h,
  resolveComponent,
  useSlots,
} from 'vue'

import FieldSkeleton from '@/components/fields/FieldSkeleton.vue'
import ItemDetailField from '@/components/fields/ItemDetailField.vue'

// CSS class lookup: row length → wrapper class
const ROW_CLASS = { 2: 'field-pair', 3: 'field-triple' }

/**
 * Composite widgets — components that manage multiple record fields.
 * They receive `dv` directly (for cross-field editing) instead of
 * going through FieldComponent's single-field STANDARD_FIELD_PROPS path.
 * No ItemDetailField wrapper — they provide their own labels internally.
 *
 * To add a new composite widget:
 *   1. Add an entry here mapping field name → async component
 *   2. Use 'widget:name' in :fields arrays (e.g. ['widget:instrumentData'])
 */
const COMPOSITE_WIDGETS = {
  instrumentData: defineAsyncComponent({
    loader: () =>
      import('@/components/fields/widget/InstrumentData/detail/index.vue'),
    loadingComponent: FieldSkeleton,
    delay: 0,
  }),
}

/**
 * Parse a field entry string into a cell descriptor.
 *
 *   'name'                     → { name: 'name',            type: 'field' }
 *   'slot:fc:is_template'      → { name: 'is_template',     type: 'fcSlot' }
 *   'slot:fc:myField:My Label' → { name: 'myField',         type: 'fcSlot', label: 'My Label' }
 *   'slot:screenFields'        → { name: 'screenFields',    type: 'rowSlot' }
 *   'widget:instrumentData'    → { name: 'instrumentData',  type: 'compositeWidget' }
 *   null / undefined            → { name: null,              type: 'placeholder' }
 */
function parseCell(entry) {
  if (entry == null) return { name: null, type: 'placeholder' }
  const parts = entry.split(':')
  if (parts[0] === 'slot') {
    if (parts[1] === 'fc') {
      return { name: parts[2], type: 'fcSlot', label: parts[3] || null }
    }
    return { name: parts[1], type: 'rowSlot' }
  }
  if (parts[0] === 'widget') {
    return { name: parts[1], type: 'compositeWidget' }
  }
  return { name: entry, type: 'field' }
}

/**
 * Build a stable Vue key for a normalized grid cell.
 *
 * Combines the cell type and name so different fields and rendering paths
 * receive distinct identities. Unnamed cells, such as placeholders, fall back
 * to their position in the current rendering context.
 */
function getCellKey(cell, index) {
  return `${cell.type}:${cell.name ?? `anonymous-${index}`}`
}

/**
 * GridCell — internal render-function component for a single cell.
 *
 * Uses a render function (instead of a template) to keep the cell rendering
 * logic in one place, avoiding duplication across row shapes.
 *
 * Handles two cell types (rowSlots are handled directly in the FieldGrid template):
 *   fcSlot → ItemDetailField + slot content, or null if no slot provided
 *   field  → ItemDetailField + (slot override | #field scoped | default FieldComponent)
 */
const GridCell = {
  props: {
    cell: { type: Object, required: true },
    dv: { type: Object, required: true },
    // Merged slots: direct $slots from FieldGrid + forwarded cellSlots from ancestor
    slots: { type: Object, default: () => ({}) },
    validationErrors: { type: Object, default: () => ({}) },
    disabledFields: { type: Object, default: () => ({}) },
  },
  setup() {
    // Resolve the globally-registered FieldComponent at setup time
    const FieldComp = resolveComponent('FieldComponent')
    return { FieldComp }
  },
  render() {
    const { cell, dv, slots, FieldComp, validationErrors, disabledFields } =
      this
    const slotFn = slots[cell.name]

    // Placeholder cell → empty div for grid alignment
    if (cell.type === 'placeholder') return h('div')

    // fcSlot with no content provided → render nothing
    if (cell.type === 'fcSlot' && !slotFn) return null

    // Composite widget → render directly with dv, no ItemDetailField wrapper.
    if (cell.type === 'compositeWidget') {
      const compositeComp = COMPOSITE_WIDGETS[cell.name]
      if (compositeComp) return h(compositeComp, { dv })
      return null
    }

    // ItemDetailField wrapper — always provided for every rendered cell
    const attrs = dv.modelAttributes[cell.name]

    // Field not found in model attributes → render debug error tag
    if (!attrs && cell.type === 'field') {
      return h(
        'div',
        {
          class: 'notification is-danger is-light p-2 m-1',
          style: 'font-size: 0.8rem',
        },
        [
          h('span', { class: 'has-text-weight-bold' }, '⚠ '),
          `Field "${cell.name}" not found in model attributes`,
        ],
      )
    }

    const wrapperProps = {
      label: attrs?.label || cell.label || cell.name,
      isEditing: dv.isEditing,
      description: attrs?.description,
      readonly: attrs?.readonly,
      mandatory: attrs?.mandatory,
    }

    // 1. Slot override — replaces inner FieldComponent
    if (slotFn) {
      return h(ItemDetailField, wrapperProps, { default: () => slotFn() })
    }

    // 2. Scoped #field slot for bulk custom rendering
    if (slots.field) {
      return h(ItemDetailField, wrapperProps, {
        default: () => slots.field({ name: cell.name }),
      })
    }

    // 3. Default: FieldComponent
    return h(ItemDetailField, wrapperProps, {
      default: () =>
        h(FieldComp, {
          'field-meta': attrs,
          data: dv.editedRecord[cell.name],
          'is-edit-mode': dv.isEditing,
          record: dv.editedRecord,
          'error-message': validationErrors[cell.name] || '',
          disabled: !!disabledFields[cell.name],
          onEdit: (value) => dv.edit({ name: cell.name }, value),
        }),
    })
  },
}

export default {
  name: 'FieldGrid',
  components: { GridCell },
  props: {
    // Array of rows. Each row is an array of field name strings
    // (or a bare string, which becomes a single-cell row).
    //   'fieldName'         → regular field with default rendering
    //   'slot:fc:fieldName' → fcSlot: ItemDetailField wrapper, slot replaces FieldComponent
    //   'slot:sectionName'  → rowSlot: raw slot content, no wrapper
    fields: { type: Array, required: true },
    // Detail-view object providing modelAttributes, editedRecord, isEditing, edit()
    dv: { type: Object, required: true },
    // Forwarded slot functions from an ancestor component (typically $slots).
    // Used to pass parent slots through an intermediary like ImagingDescription.
    cellSlots: { type: Object, default: () => ({}) },
    // { fieldName: 'error message' } → forwarded as :error-message on default FieldComponent
    validationErrors: { type: Object, default: () => ({}) },
    // { fieldName: true/false } → forwarded as :disabled on default FieldComponent
    disabledFields: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const directSlots = useSlots()

    // Merge direct $slots (from FieldGrid's parent template) with forwarded
    // cellSlots (from further up the tree). Direct slots take priority.
    const mergedSlots = computed(() => ({
      ...props.cellSlots,
      ...directSlots,
    }))

    // Normalize: wrap bare strings in arrays, parse slot: prefixes, drop empties.
    // Rows where all non-null cells share the same name are marked as spanning.
    const normalizedFields = computed(() =>
      props.fields
        .map((row) => {
          const cells = (Array.isArray(row) ? row : [row]).map(parseCell)
          const realCells = cells.filter((c) => c.type !== 'placeholder')
          const isSpan =
            cells.length > 1 &&
            realCells.length >= 1 &&
            realCells.every((c) => c.name === realCells[0]?.name)
          return { cells, isSpan, rawLength: cells.length }
        })
        .filter((row) => row.cells.length > 0),
    )

    return { normalizedFields, mergedSlots, ROW_CLASS, getCellKey }
  },
}
</script>
